import { useRef, useEffect, useLayoutEffect, useState, useMemo, useCallback, type KeyboardEvent } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import ReactMarkdown from "react-markdown";
import { useChatStore } from "@/stores/chatStore";
import { useChatStream } from "./useChatStream";
import type * as THREE from "three";
import "./speechBubble.css";

interface SpeechBubbleProps {
  avatarPosition: React.RefObject<THREE.Vector3>;
  yOffset?: number;
}

export default function SpeechBubble({
  avatarPosition,
  yOffset = 2,
}: SpeechBubbleProps) {
  const groupRef = useRef<THREE.Group>(null!);
  const inputRef = useRef<HTMLInputElement>(null);
  const gl = useThree((s) => s.gl);
  const portalTarget = useMemo(
    () => ({ current: typeof document !== "undefined" ? document.body : null }),
    []
  ) as React.MutableRefObject<HTMLElement>;

  const clearCanvasHover = useCallback(() => {
    const canvas = gl.domElement;
    canvas.dispatchEvent(
      new PointerEvent("pointermove", {
        clientX: -10000,
        clientY: -10000,
        bubbles: true,
        pointerType: "mouse",
      })
    );
  }, [gl]);

  const isOpen = useChatStore((s) => s.isOpen);
  const messages = useChatStore((s) => s.messages);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const currentZone = useChatStore((s) => s.currentZone);
  const openChat = useChatStore((s) => s.openChat);
  const closeChat = useChatStore((s) => s.closeChat);
  const { sendMessage } = useChatStream();

  const [input, setInput] = useState("");

  useFrame(() => {
    if (!groupRef.current || !avatarPosition.current) return;
    const pos = avatarPosition.current;
    groupRef.current.position.set(pos.x, pos.y + yOffset, pos.z);
  });

  useEffect(() => {
    if (isOpen && !isStreaming) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isStreaming]);

  // Callback ref that attaches a MutationObserver the moment the element
  // mounts. drei <Html> portals children with timing that breaks regular
  // useEffect+useRef (ref is null when the effect runs), so we need to
  // hook in synchronously at attach time.
  const moRef = useRef<MutationObserver | null>(null);
  const scrollEl = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const detachScroll = useRef<(() => void) | null>(null);

  // Floating scrollbar: the native one is hidden (see .chat-scroll in
  // speechBubble.css) and this thin thumb is drawn over the content instead.
  // It fades in while scrolling or hovering and takes no layout width.
  const updateThumb = useCallback(() => {
    const el = scrollEl.current;
    const thumb = thumbRef.current;
    if (!el || !thumb) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflow = scrollHeight - clientHeight;
    if (overflow <= 1) {
      thumb.style.display = "none";
      return;
    }
    const inset = 6;
    const track = clientHeight - inset * 2;
    const h = Math.max(28, (clientHeight / scrollHeight) * track);
    const y = inset + (scrollTop / overflow) * (track - h);
    thumb.style.display = "block";
    thumb.style.height = `${h}px`;
    thumb.style.transform = `translateY(${y}px)`;
  }, []);

  const hideTimer = useRef<number | undefined>(undefined);
  const flashThumb = useCallback(() => {
    const thumb = thumbRef.current;
    if (!thumb) return;
    thumb.dataset.active = "true";
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (thumb.dataset.dragging !== "true") delete thumb.dataset.active;
    }, 900);
  }, []);

  const scrollCallbackRef = useCallback((el: HTMLDivElement | null) => {
    scrollEl.current = el;
    moRef.current?.disconnect();
    moRef.current = null;
    detachScroll.current?.();
    detachScroll.current = null;
    if (!el) return;
    const pin = () => {
      el.scrollTop = el.scrollHeight;
      updateThumb();
    };
    pin();
    const mo = new MutationObserver(pin);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    moRef.current = mo;

    const onScroll = () => {
      updateThumb();
      flashThumb();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(updateThumb);
    ro.observe(el);
    detachScroll.current = () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [updateThumb, flashThumb]);

  const thumbCallbackRef = useCallback((thumb: HTMLDivElement | null) => {
    thumbRef.current = thumb;
    updateThumb();
  }, [updateThumb]);

  // Drag the thumb to scroll
  const onThumbPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollEl.current;
    const thumb = thumbRef.current;
    if (!el || !thumb) return;
    e.preventDefault();
    e.stopPropagation();
    thumb.setPointerCapture(e.pointerId);
    thumb.dataset.dragging = "true";
    thumb.dataset.active = "true";
    const startY = e.clientY;
    const startTop = el.scrollTop;
    const ratio =
      (el.scrollHeight - el.clientHeight) /
      Math.max(1, el.clientHeight - 12 - thumb.offsetHeight);
    const move = (ev: PointerEvent) => {
      el.scrollTop = startTop + (ev.clientY - startY) * ratio;
    };
    const up = () => {
      delete thumb.dataset.dragging;
      thumb.removeEventListener("pointermove", move);
      thumb.removeEventListener("pointerup", up);
      thumb.removeEventListener("pointercancel", up);
      flashThumb();
    };
    thumb.addEventListener("pointermove", move);
    thumb.addEventListener("pointerup", up);
    thumb.addEventListener("pointercancel", up);
  }, [flashThumb]);

  // Also pin on every message change (covers the synchronous case where
  // layout finishes before the observer's microtask)
  const lastMessageContent = messages[messages.length - 1]?.content ?? "";
  useLayoutEffect(() => {
    const el = scrollEl.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, lastMessageContent]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput("");
    await sendMessage(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const ZONE_LABELS: Record<string, string> = {
    arch: "Architecture",
    varch: "Virtual Architecture",
    product: "Product Design",
    projects: "Software Development",
    center: "Hub",
  };

  const ZONE_PROMPTS: Record<string, { line1: string; line2: string }> = {
    arch: { line1: "Architecture projects here.", line2: "Click to ask!" },
    varch: { line1: "Virtual architecture work.", line2: "Click to ask!" },
    product: { line1: "Product design projects.", line2: "Click to ask!" },
    projects: { line1: "My projects are here.", line2: "Click to ask!" },
    center: { line1: "Hi, I'm Parker!", line2: "Click to chat." },
  };
  const collapsedPrompt =
    (currentZone ? ZONE_PROMPTS[currentZone] : undefined) ?? {
      line1: "Hi, I'm Parker!",
      line2: "Click to chat.",
    };

  const ZONE_PRESETS: Record<string, string[]> = {
    arch: [
      "What architectural projects has Parker worked on?",
      "Tell me about SFO Terminal 3 West",
      "What was Parker's role in airport projects?",
      "What computational design tools does Parker use?",
      "Tell me about the China developments",
      "What was the 777 Airport Boulevard project?",
    ],
    varch: [
      "What metaverse projects has Parker built?",
      "Tell me about Convergence",
      "What is OVA.design?",
      "Has Parker built anything in Decentraland?",
      "What software does Parker use for virtual architecture?",
      "Tell me about Virtual Vibes",
    ],
    product: [
      "What is Infini Loops?",
      "What product design work has Parker done?",
      "What design tools does Parker use?",
      "How did Parker get into product design?",
      "What materials does Parker work with?",
      "Tell me about Parker's 3D printing work",
    ],
    projects: [
      "What is Parker working on lately?",
      "Tell me about Parallel AI",
      "What web tech does Parker use?",
      "What is the parkercai.com site built with?",
      "Tell me about CRIA Design",
      "Has Parker built any AI applications?",
    ],
    center: [
      "Who is Parker?",
      "What are Parker's main skills?",
      "What's Parker working on lately?",
      "How did Parker transition from architecture to web dev?",
      "What tech stack does Parker use?",
      "How can I contact Parker?",
    ],
  };
  const askedPresets = useChatStore((s) => s.askedPresets);
  const markPresetAsked = useChatStore((s) => s.markPresetAsked);
  const allPresets =
    (currentZone ? ZONE_PRESETS[currentZone] : undefined) ?? ZONE_PRESETS.center;
  const presets = allPresets.filter((q) => !askedPresets.includes(q)).slice(0, 3);

  return (
    <group ref={groupRef}>
      <Html
        portal={portalTarget}
        style={{
          pointerEvents: "auto",
          transform: "translate(-50%, -100%)",
        }}
        zIndexRange={[100, 0]}
      >
        {!isOpen ? (
          /* Collapsed: click-to-chat prompt (zone-aware) */
          <div
            onPointerEnter={clearCanvasHover}
            onClick={openChat}
            className="cursor-pointer select-none animate-bounce-slow"
          >
            <div
              key={currentZone ?? "default"}
              className="bg-white text-gray-800 text-xs font-semibold
                px-4 py-2 rounded-[16px] shadow-lg text-center leading-tight
                whitespace-nowrap
                hover:scale-105 transition-transform duration-150
                animate-zone-pop"
            >
              <div>{collapsedPrompt.line1}</div>
              <div className="text-gray-500 font-normal mt-0.5">
                {collapsedPrompt.line2}
              </div>
            </div>
            {/* Tip */}
            <div className="flex justify-center -mt-px">
              <div className="w-0 h-0
                border-l-[6px] border-l-transparent
                border-r-[6px] border-r-transparent
                border-t-[8px] border-t-white" />
            </div>
          </div>
        ) : (
          /* Expanded: manga-style chat bubble */
          <div
            onPointerEnter={clearCanvasHover}
            className="relative"
            style={{ transform: "translateY(-23px)" }}
          >
            <div
              className="bg-white rounded-[28px] overflow-hidden ring-1 ring-black/5
                shadow-[0_12px_40px_-8px_rgba(15,23,42,0.35)]"
              style={{ width: "min(320px, calc(100vw - 2rem))" }}
            >

              {/* Header - minimal */}
              <div className="flex items-center justify-between pl-4 pr-2.5 pt-3 pb-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2 h-2 rounded-full corner-round shrink-0 bg-gradient-to-br from-[#7877EE] to-[#75D9C7]"
                    aria-hidden
                  />
                  <span className="text-sm font-semibold tracking-tight text-gray-900 truncate">
                    Parker's AMA bot
                  </span>
                  {currentZone && (
                    <span className="text-[10px] font-medium text-gray-500 bg-gray-100 rounded-[10px] px-2 py-0.5 whitespace-nowrap">
                      {ZONE_LABELS[currentZone] ?? currentZone}
                    </span>
                  )}
                </div>
                <button
                  onClick={closeChat}
                  aria-label="Close chat"
                  className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full corner-round
                    w-6 h-6 flex items-center justify-center transition-colors shrink-0"
                >
                  <svg viewBox="0 0 16 16" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                </button>
              </div>

              {/* Messages (native scrollbar hidden; floating thumb overlays it) */}
              <div className="relative group/scroll">
              <div
                ref={scrollCallbackRef}
                className="chat-scroll overflow-y-auto px-3 py-3 space-y-2"
                style={{ maxHeight: "min(50vh, 420px)" }}
              >
                {messages.length === 0 && (
                  <div className="text-center text-xs py-2">
                    <p className="font-medium text-gray-500">Hey there!</p>
                    <p className="text-gray-400 mt-0.5">
                      Ask me anything, or try a quick prompt below.
                    </p>
                  </div>
                )}
                {messages.map((msg, i) => {
                  const isUser = msg.role === "user";
                  const isLastStreaming =
                    isStreaming && i === messages.length - 1 && !isUser;
                  return (
                    <div
                      key={i}
                      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[85%] px-3 py-2 text-xs leading-relaxed
                          ${isUser
                            ? "bg-gray-900 text-white rounded-[18px] rounded-br-[6px]"
                            : "bg-gray-100/80 text-gray-700 rounded-[18px] rounded-bl-[6px]"
                          }`}
                      >
                        {isUser ? (
                          <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                        ) : (
                          <div className="markdown-bubble break-words">
                            <ReactMarkdown
                              components={{
                                p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
                                ul: ({ children }) => <ul className="list-disc pl-4 mb-1.5 last:mb-0 space-y-0.5">{children}</ul>,
                                ol: ({ children }) => <ol className="list-decimal pl-4 mb-1.5 last:mb-0 space-y-0.5">{children}</ol>,
                                li: ({ children }) => <li className="leading-snug">{children}</li>,
                                strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                                em: ({ children }) => <em className="italic">{children}</em>,
                                code: ({ children }) => <code className="bg-gray-200/70 px-1 py-0.5 rounded text-[11px] font-mono">{children}</code>,
                                a: ({ href, children }) => (
                                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                                    {children}
                                  </a>
                                ),
                                h1: ({ children }) => <h3 className="font-semibold text-sm mb-1">{children}</h3>,
                                h2: ({ children }) => <h3 className="font-semibold text-sm mb-1">{children}</h3>,
                                h3: ({ children }) => <h3 className="font-semibold text-xs mb-1">{children}</h3>,
                              }}
                            >
                              {msg.content}
                            </ReactMarkdown>
                          </div>
                        )}
                        {isLastStreaming && (
                          <span className="inline-block w-1 h-3 ml-0.5 bg-gray-300 animate-pulse align-middle" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div
                ref={thumbCallbackRef}
                onPointerDown={onThumbPointerDown}
                className="chat-scroll-thumb absolute top-0 right-1 w-1 rounded-full corner-round bg-gray-900/25
                  opacity-0 group-hover/scroll:opacity-100 hover:w-1.5 hover:bg-gray-900/40
                  transition-[opacity,width,background-color] duration-200 cursor-default touch-none"
                style={{ display: "none" }}
              />
              </div>

              {/* Quick-prompt chips (persistent, zone-aware, rotates as asked) */}
              {presets.length > 0 && (
                <div className="px-2.5 pt-2.5 pb-1.5 border-t border-gray-100">
                  <div className="grid grid-cols-3 gap-1.5 items-stretch">
                    {presets.map((q) => (
                      <button
                        key={q}
                        onClick={() => {
                          if (isStreaming) return;
                          markPresetAsked(q);
                          sendMessage(q);
                        }}
                        disabled={isStreaming}
                        className="text-[10px] leading-tight font-medium px-2.5 py-2 rounded-[14px]
                          bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-900
                          border border-gray-200/80 hover:border-gray-300
                          transition-colors text-left whitespace-normal break-words
                          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <div
                className="mx-2.5 mb-2.5 mt-1 flex items-center gap-1 bg-gray-50 border border-gray-200/80
                  rounded-[22px] pl-3.5 pr-1 py-1 transition-colors
                  focus-within:border-gray-300 focus-within:bg-white"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={isStreaming ? "Thinking..." : "Ask something..."}
                  disabled={isStreaming}
                  className="flex-1 min-w-0 bg-transparent py-1
                    text-xs text-gray-700 placeholder-gray-400
                    focus:outline-none disabled:opacity-50"
                />
                <button
                  onClick={handleSend}
                  disabled={isStreaming || !input.trim()}
                  aria-label="Send"
                  className="w-7 h-7 shrink-0 rounded-full corner-round flex items-center justify-center
                    bg-gray-900 hover:bg-gray-700 disabled:bg-gray-200
                    text-white disabled:text-gray-400
                    transition-colors disabled:cursor-not-allowed"
                >
                  <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 13V3M3.5 7.5L8 3l4.5 4.5" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Manga bubble tail */}
            <div className="flex justify-center -mt-px">
              <div className="w-0 h-0
                border-l-[8px] border-l-transparent
                border-r-[8px] border-r-transparent
                border-t-[12px] border-t-white" />
            </div>
          </div>
        )}
      </Html>
    </group>
  );
}
